import { getContentEnv } from './Network';

// 'embedded' is a reserved placeholder for future non-Mondly iframe integrations
// (e.g. WizPortal). detectAuthMode() does not return it yet — all iframe contexts
// currently route to 'mondly' because the Mondly bundle's getUserToken() handles
// both native and web-iframe delivery transparently. This will be activated once
// a non-Mondly embedded integration defines an explicit identification signal.
export type AuthMode = 'mondly' | 'standalone' | 'embedded';

/**
 * Returns true if the current build targets a known non-prod content environment.
 * Used to gate the ?testWeb fake-user bypass in all three call sites.
 *
 * Reads process.env.REACT_APP_CONTENT_ENV directly — intentionally NOT getContentEnv().
 * getContentEnv() reads ?contentEnv off the URL, so using it here would let anyone pass
 * ?contentEnv=dev&testWeb in a prod build and re-enable the bypass at runtime.
 * The raw env var is baked in at build time and cannot be overridden via URL params.
 *
 * Fail-closed allowlist: an unset, empty, or misspelled value returns false.
 */
export function isTestWebEnv(): boolean {
  return ['dev', 'stage'].includes(process.env.REACT_APP_CONTENT_ENV ?? '');
}

const IES_TOKEN_KEY = 'tutoring_ies_jwt';
const OAUTH_STATE_KEY = 'tutoring_oauth_state';
const REDIRECT_COUNT_KEY = 'tutoring_redirect_count';
const AUTH_ERROR_KEY = 'auth_error';
const MAX_REDIRECTS = 2;

/**
 * Detects the authentication mode based on the runtime environment:
 * - 'mondly': native Android/iOS WebView, any iframe (Mondly web included), wizard hash, or testWeb
 * - 'standalone': direct browser access — no native bridge and no parent frame
 * - 'embedded': reserved placeholder, not yet returned (see AuthMode)
 */
export function detectAuthMode(): AuthMode {
  const params = new URLSearchParams(window.location.search);

  // Wizard hash: always treated as mondly mode regardless of environment.
  if (params.has('hash')) {
    return 'mondly';
  }

  // testWeb (fake user): only available in known non-prod content environments.
  // See isTestWebEnv() for why process.env is read directly instead of getContentEnv().
  // Use params.has() — not params.get() — because get() returns "" (falsy) when
  // the param is present without a value (e.g. ?testWeb), causing the check to fail.
  if (isTestWebEnv() && params.has('testWeb')) {
    return 'mondly';
  }

  // Real Mondly native WebView (Android or iOS): OS-injected bridges.
  // These are only present inside a genuine native WebView — never in any browser context.
  //   - Android: window.mondlyNativeAndroid  (JavascriptInterface)
  //   - iOS:     window.webkit.messageHandlers.mondlyNativeIos  (WKScriptMessageHandler)
  if (window.mondlyNativeAndroid || window.webkit?.messageHandlers?.mondlyNativeIos) {
    return 'mondly';
  }

  // Mondly web (iframe): the bundle is always loaded and sets window.mondlyNative on
  // every page load regardless of context. Inside an iframe, window.mondlyNative.getUserToken()
  // routes via window.top.postMessage to the Mondly web parent app, which responds with
  // the token — the same getUserToken() call works transparently across native and web.
  //
  // The 'embedded' mode (WizPortal or future integrations) is a placeholder. It is not
  // currently distinguishable from a Mondly web iframe without an explicit signal from the
  // parent (e.g. a dedicated query param or a different postMessage handshake). All iframe
  // contexts are treated as Mondly for now; this will be revisited when a non-Mondly
  // embedded integration is designed.
  if (window.parent !== window) {
    return 'mondly';
  }

  // Direct browser access: no native bridge, no parent frame → standalone IES OAuth2.
  return 'standalone';
}

/**
 * Returns the callback URL for IES OAuth2 redirect.
 * Matches the Portal pattern: {origin}/api/auth/callback/ies
 */
export function getCallbackUrl(): string {
  return `${window.location.origin}/api/auth/callback/ies`;
}

/**
 * Returns the IES login URL for redirect-based auth.
 * Generates and stores a random state parameter to prevent CSRF attacks.
 */
export function getIESLoginUrl(): string {
  const env = getContentEnv();

  let iesIAMUrl: string;
  const clientId = process.env.REACT_APP_IES_CLIENT_ID || 'WIZPTL-Client';
  const centralizedClientId =
    process.env.REACT_APP_IES_CENTRALIZED_CLIENT_ID || 'AvQ4Cvyidqga3McpgajgwDksUYS4TRBF';

  switch (env) {
    case 'prod':
      iesIAMUrl = 'https://iam.pearson.com';
      break;
    case 'stage':
      iesIAMUrl = 'https://iam-stage.pearson.com';
      break;
    default:
      iesIAMUrl = 'https://iam-stage.pearson.com';
      break;
  }

  // Generate and store a random state for CSRF protection
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  const state = Array.from(randomBytes, (b) => b.toString(16).padStart(2, '0')).join('');
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  // Build the authorize URL (this is the "goto" parameter)
  const authorizeUrl =
    `${iesIAMUrl}/auth/oauth2/pearson/authorize` +
    `?client_id=${encodeURIComponent(clientId)}` +
    `&scope=openid` +
    `&response_type=code` +
    `&state=${encodeURIComponent(state)}` +
    `&redirect_uri=${encodeURIComponent(getCallbackUrl())}`;

  // Build centralized login URL
  const loginUrl =
    `${iesIAMUrl}/auth/centralized-login/` +
    `?clientId=${encodeURIComponent(centralizedClientId)}` +
    `&goto=${encodeURIComponent(authorizeUrl)}`;

  return loginUrl;
}

/**
 * Returns the BFF/IAM base URL for auth endpoints.
 */
function getIamBaseUrl(): string {
  if (process.env.REACT_APP_IAM_BASE_URL) {
    return process.env.REACT_APP_IAM_BASE_URL;
  }
  const env = getContentEnv();
  switch (env) {
    case 'prod':
      return 'https://bff.newwizme.com.br/bff';
    case 'stage':
      return 'https://bff-stg.newwizme.com.br/bff';
    default:
      return 'https://bff-dev.newwizme.com.br/bff';
  }
}

/**
 * Exchanges the IES authorization code for a token via the IAM/BFF service.
 */
export async function exchangeCodeForToken(code: string): Promise<string | null> {
  try {
    const iamUrl = getIamBaseUrl();

    const response = await fetch(`${iamUrl}/iam/api/v1/signin/sso/ies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        redirectUri: getCallbackUrl(),
        clientId: process.env.REACT_APP_IES_CLIENT_ID || 'WIZPTL-Client',
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      console.error('Failed to exchange code for token:', response.status, errorBody);
      return null;
    }

    const data = await response.json();
    return data?.data?.token?.token || null;
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    return null;
  }
}

/**
 * Stores the JWT token in sessionStorage.
 */
export function storeToken(token: string): void {
  sessionStorage.setItem(IES_TOKEN_KEY, token);
  sessionStorage.removeItem(REDIRECT_COUNT_KEY); // Reset loop cap on successful auth
  sessionStorage.removeItem(AUTH_ERROR_KEY); // Clear any previous auth error on successful auth
}

/**
 * Retrieves the stored JWT token.
 */
export function getStoredToken(): string | null {
  return sessionStorage.getItem(IES_TOKEN_KEY);
}

/**
 * Clears the stored auth data.
 * Does NOT reset the redirect loop counter — that is only reset on successful
 * auth (storeToken) or intentional logout (performLogout), so that clearing an
 * invalid token does not bypass the redirect cap.
 */
export function clearStoredAuth(): void {
  sessionStorage.removeItem(IES_TOKEN_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
}

/**
 * Checks if the current URL is the auth callback page.
 * Only matches the specific callback path with a code parameter.
 */
export function isAuthCallback(): boolean {
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname === '/api/auth/callback/ies' && params.has('code');
}

/**
 * Returns true if an auth exchange is currently in progress.
 */
export function isAuthExchangeInProgress(): boolean {
  return sessionStorage.getItem('auth_exchange_in_progress') === 'true';
}

/**
 * Handles the OAuth callback by extracting the code and exchanging it for a token.
 * Returns the JWT token on success.
 */
let authCallbackInProgress = false;
export async function handleAuthCallback(): Promise<string | null> {
  // Guard against double calls (React re-renders while code is still in URL)
  if (authCallbackInProgress) {
    console.warn('[AuthStrategy] handleAuthCallback already in progress, skipping duplicate call');
    return null;
  }
  authCallbackInProgress = true;
  sessionStorage.setItem('auth_exchange_in_progress', 'true');

  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const returnedState = params.get('state');

  // Validate state parameter (CSRF protection)
  const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  if (!returnedState || returnedState !== savedState) {
    console.error('OAuth state mismatch — possible CSRF attack');
    authCallbackInProgress = false;
    sessionStorage.removeItem('auth_exchange_in_progress');
    return null;
  }
  sessionStorage.removeItem(OAUTH_STATE_KEY);

  if (!code) {
    console.error('No code parameter in auth callback');
    authCallbackInProgress = false;
    sessionStorage.removeItem('auth_exchange_in_progress');
    return null;
  }

  // Clean up URL — use full navigation so React Router detects the route change
  const cleanUrl = `${window.location.origin}/`;

  const token = await exchangeCodeForToken(code);

  if (token) {
    storeToken(token);
  }

  authCallbackInProgress = false;
  sessionStorage.removeItem('auth_exchange_in_progress');

  // Navigate to root so React Router picks up the "/" route
  window.history.replaceState({}, document.title, cleanUrl);

  return token;
}

/**
 * Redirects to IES login page.
 */
export function redirectToIESLogin(): void {
  const count = parseInt(sessionStorage.getItem(REDIRECT_COUNT_KEY) || '0', 10);
  if (count >= MAX_REDIRECTS) {
    console.error(
      '[AuthStrategy] Authentication failed repeatedly — IAM may be unavailable. Stopping redirect loop.',
    );
    sessionStorage.setItem(AUTH_ERROR_KEY, 'loop_cap');
    return;
  }
  sessionStorage.removeItem(AUTH_ERROR_KEY);
  sessionStorage.setItem(REDIRECT_COUNT_KEY, String(count + 1));
  window.location.href = getIESLoginUrl();
}

/**
 * Returns the current auth error key, or null if there is none.
 * - 'loop_cap': redirect loop cap was reached (BFF likely unavailable)
 */
export function getAuthError(): string | null {
  return sessionStorage.getItem(AUTH_ERROR_KEY);
}

/**
 * Returns the IES logout URL that clears the session.
 * Uses the authorize URL directly as goto (not the centralized-login URL),
 * to avoid nested URL encoding that causes IES to double-decode and
 * lose the authorize parameters (scope, redirect_uri, etc.).
 * Flow: logout → authorize endpoint → (no session) → IES login → our callback.
 */
function getIESLogoutUrl(): string {
  const env = getContentEnv();
  let iesIAMUrl: string;
  switch (env) {
    case 'prod':
      iesIAMUrl = 'https://iam.pearson.com';
      break;
    default:
      iesIAMUrl = 'https://iam-stage.pearson.com';
      break;
  }

  const clientId = process.env.REACT_APP_IES_CLIENT_ID || 'WIZPTL-Client';

  // Generate a fresh OAuth state for the post-logout login flow.
  // clearStoredAuth() already removed the old state; this sets a new one.
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  const state = Array.from(randomBytes, (b) => b.toString(16).padStart(2, '0')).join('');
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  // The authorize URL is an IES-internal URL so it passes IES's allowlist.
  // One level of encoding avoids the double-decode issue with nested URLs.
  const authorizeUrl =
    `${iesIAMUrl}/auth/oauth2/pearson/authorize` +
    `?client_id=${encodeURIComponent(clientId)}` +
    `&scope=openid` +
    `&response_type=code` +
    `&state=${encodeURIComponent(state)}` +
    `&redirect_uri=${encodeURIComponent(getCallbackUrl())}`;

  return `${iesIAMUrl}/auth/UI/Logout?goto=${encodeURIComponent(authorizeUrl)}`;
}

/**
 * Performs logout - clears local tokens and ends the IES session.
 */
export function performLogout(): void {
  clearStoredAuth();
  sessionStorage.removeItem(REDIRECT_COUNT_KEY); // Reset loop cap on intentional logout
  sessionStorage.removeItem(AUTH_ERROR_KEY); // Clear any previous auth error on intentional logout
  window.location.href = getIESLogoutUrl();
}

/**
 * Sets up a postMessage listener for embedded mode.
 * Parent app sends: { type: 'tutoring-auth', token: '...' }
 * Returns a cleanup function.
 *
 * If allowedOrigins is empty, all messages are rejected (secure by default).
 */
export function setupEmbeddedAuthListener(
  onToken: (token: string) => void,
  allowedOrigins?: string[],
): () => void {
  const handler = (event: MessageEvent) => {
    // Reject if no origins configured (secure by default)
    if (!allowedOrigins || allowedOrigins.length === 0 || allowedOrigins[0] === '') {
      return;
    }

    if (!allowedOrigins.includes(event.origin)) {
      return;
    }

    if (event.data && event.data.type === 'tutoring-auth' && event.data.token) {
      onToken(event.data.token);
    }
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
